import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { Button } from '../Button/Button';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { SetReadOnly, SetSubCard } from '../SettingsShell/rows';
import { SetChip, SetSwitch } from '../SettingsShell/controls';
import { REMINDERS } from '../../settings-reminders/mockData';
import {
  audienceLabel,
  beforeFooter,
  beforeSection,
  checksInWords,
  procedureSections,
  whenLabel,
} from '../../settings-reminders/derive';
import { RM_CHANNEL_ICON, RM_CHANNEL_LABEL } from '../../settings-reminders/types';
import type { RmSection, RmStep } from '../../settings-reminders/types';

export interface RemindersViewProps {
  /** `before` · `missed` · `review` · a procedure key. */
  onEditSection?: (key: string) => void;
  onOpenAppointmentReasons?: () => void;
  onOpenPracticeManagement?: () => void;
  onToggleStep?: (sectionKey: string, stepId: string, on: boolean) => void;
  onToggleSection?: (key: string, on: boolean) => void;
}

/**
 * Settings › AI Brain › Appointment Reminders — **a summary** (amendment 25 §2,
 * board `RM0Settings`). The board this replaces is titled *SUPERSEDED*.
 *
 * The old page was an editor: six cards of controls, and the message bodies two
 * clicks down inside *Content →* modals that could not show a preview. The
 * messages are the thing being edited, so they moved to a real two-step editor
 * and **nothing on this page edits a message**. What stays is the shape: what
 * goes out, when, to whom, and on or off.
 *
 * Also removed, all of it deliberately: the purpose banner, the *15+ days ahead*
 * condition, and every card-footer paragraph explaining the design to the reader
 * (*"Who a step goes to is a condition…"*, *"Two shipped defaults are
 * deliberate…"*, *"Escalation is a threshold…"*). A practice does not need the
 * reasoning; it needs the rows.
 */
export function RemindersView({
  onEditSection,
  onOpenAppointmentReasons,
  onOpenPracticeManagement,
  onToggleStep,
  onToggleSection,
}: RemindersViewProps) {
  const before = beforeSection();
  const missed = REMINDERS.sections.find((s) => s.kind === 'missed');
  const procedures = procedureSections();
  const review = REMINDERS.review;
  const safety = REMINDERS.safety;

  return (
    <div className="min-w-0 flex-1">
      <SettingsPageHeader
        eyebrow="Settings · AI Brain"
        titleLead="Appointment"
        titleAccent="Reminders"
        sub="Confirms each appointment, reminds the patient, checks on them after surgery, and follows up when they miss a visit. Messages go out in each patient’s own language."
      />

      <SettingsCards>
        {/* 01 — Before the visit */}
        <SettingsCard
          n="01"
          id="before"
          title="Before the visit"
          sub="Sent to everyone with an appointment. Each step can go to everyone, or only to patients who have or haven’t confirmed."
          headRight={
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                onEditSection?.('before');
              }}
            >
              Edit messages
            </Button>
          }
        >
          {before.steps.map((step, i) => (
            <StepRow
              key={step.id}
              step={step}
              section={before}
              last={i === before.steps.length - 1}
              onToggle={(on) => {
                onToggleStep?.(before.key, step.id, on);
              }}
            />
          ))}
          {/* Counted — "5 of 6 steps · 4 on" was written down before, beside the
              steps it was meant to describe. */}
          <SettingsNote>{beforeFooter(before)}</SettingsNote>
        </SettingsCard>

        {/* 02 — After the visit */}
        <SettingsCard
          n="02"
          id="after"
          title="After the visit"
          sub="A post-op check for each procedure that needs one, and a follow-up when a patient misses their appointment."
        >
          {missed ? (
            <AfterRow
              title={missed.title}
              tag="Follow-up"
              words="One text, 2 hours after the start time · offers to rebook"
              on={missed.on}
              onEdit={() => {
                onEditSection?.(missed.key);
              }}
              onToggle={(on) => {
                onToggleSection?.(missed.key, on);
              }}
            />
          ) : null}
          {procedures.map((p, i) => (
            <AfterRow
              key={p.key}
              title={p.title}
              tag="Post-op check"
              words={checksInWords(p)}
              changedByPractice={p.changedByPractice === true}
              on={p.on}
              last={i === procedures.length - 1}
              // A procedure with no checks gets no Edit — there is nothing to open.
              onEdit={
                p.hasChecks === false
                  ? undefined
                  : () => {
                      onEditSection?.(p.key);
                    }
              }
              onToggle={
                p.hasChecks === false
                  ? undefined
                  : (on) => {
                      onToggleSection?.(p.key, on);
                    }
              }
            />
          ))}
          <SettingsNote>
            Which procedures get a check comes from{' '}
            <button
              type="button"
              onClick={onOpenAppointmentReasons}
              className="font-semibold text-[var(--brand-indigo)] hover:underline"
            >
              Appointment reasons
            </button>
            . Post-op checks only go to patients whose visit was completed.
          </SettingsNote>
        </SettingsCard>

        {/* 03 — Post-op safety */}
        <SettingsCard
          n="03"
          id="safety"
          title="Post-op safety"
          accent="red"
          sub="When ELVA hands a post-op patient to a person."
        >
          <SettingsRow
            label="Pain score at or above"
            desc="Every post-op check asks for a score from 1 to 10."
            control={
              <span className="inline-flex h-8 min-w-[52px] items-center justify-center rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 font-[var(--font-mono)] text-[14px] font-bold text-[var(--text-primary)]">
                {safety.painScoreAtOrAbove}
              </span>
            }
          />
          <SettingsRow
            label="Anything on the procedure’s complication list"
            desc="Each procedure has its own list — set when you edit it above."
            control={<SetReadOnly value="per procedure" />}
          />
          <SettingsRow label="During opening hours" control={<SetReadOnly value={safety.inHours} />} />
          <SettingsRow label="Outside opening hours" control={<SetReadOnly value={safety.outOfHours} />} />
          <SettingsRow label="ELVA never">
            <div className="mt-2 text-[12.5px] leading-[1.7] text-[var(--text-secondary)]">
              {safety.neverDo.join(' · ')}
            </div>
          </SettingsRow>
          <SettingsRow
            last
            label="Every post-op message ends with"
            desc="Can’t be changed."
          >
            <div className="mt-2 rounded-[10px] bg-[#FBF3E4] px-3.5 py-2.5 text-[12.5px] italic leading-[1.6] text-[var(--text-secondary)]">
              “If it gets worse, call us or 911 now — don’t wait for us to call you.”
            </div>
          </SettingsRow>
        </SettingsCard>

        {/* 04 — Review ask */}
        <SettingsCard
          n="04"
          id="review"
          title="Review ask"
          sub="One text asking for a Google review, a few days after a visit."
          headRight={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                onEditSection?.('review');
              }}
            >
              Edit message
            </Button>
          }
        >
          <SettingsRow
            label="Ask for a review"
            control={
              <SetSwitch
                label="Ask for a review"
                checked={review.on}
                onChange={(on) => {
                  onToggleSection?.('review', on);
                }}
              />
            }
          />
          <SettingsRow
            label="At most"
            desc="Per patient, whatever they come in for."
            control={<SetReadOnly value={`Once every ${String(review.atMostPerMonths)} months`} />}
          />
          <SettingsRow
            label="After these visits"
            control={
              <span className="inline-flex items-center gap-1.5">
                {review.afterCategories.map((c) => (
                  <SetChip key={c}>{c}</SetChip>
                ))}
                <button type="button" className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
                  Change
                </button>
              </span>
            }
          />
          <SettingsRow
            label="Sent"
            control={<SetReadOnly value={`${String(review.daysAfter)} days after the visit`} />}
          />
          <SettingsRow
            label="Link"
            desc="One per location, from Practice management."
            control={
              <SetReadOnly
                value={
                  <button
                    type="button"
                    onClick={onOpenPracticeManagement}
                    className="font-semibold text-[var(--brand-indigo)] hover:underline"
                  >
                    {review.destination}
                  </button>
                }
              />
            }
          />
          <SettingsRow
            last
            label="If a post-op check reported a problem"
            control={<SetReadOnly value="Waits until it’s resolved" />}
          />
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter location="Settings · AI Brain · Appointment Reminders" />
    </div>
  );
}

/** One step of *Before the visit*: when · channel · who gets it · on. */
function StepRow({
  step,
  section,
  last,
  onToggle,
}: {
  step: RmStep;
  section: RmSection;
  last?: boolean;
  onToggle: (on: boolean) => void;
}) {
  const who = audienceLabel(step);
  const [plain, accent] = who.includes(' · ') ? who.split(' · ') : [who, undefined];
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-3.5"
      style={last === true ? undefined : { borderBottom: '1px solid #edf0f6' }}
    >
      <span className="w-[120px] shrink-0 text-[13px] font-semibold text-[var(--text-primary)]">
        {whenLabel(step, section.kind)}
      </span>
      <span className="inline-flex w-[92px] shrink-0 items-center gap-1.5 text-[12.5px] text-[var(--text-secondary)]">
        <Icon kind={RM_CHANNEL_ICON[step.channel] as IconKind} size={12} stroke={2} />
        {RM_CHANNEL_LABEL[step.channel]}
      </span>
      <span className="min-w-0 flex-1 text-[12.5px] text-[var(--text-secondary)]">
        {plain}
        {/* The second half in accent, because it is the interesting half: one
            step, two messages. */}
        {accent === undefined ? null : (
          <>
            {' · '}
            <span className="font-semibold text-[var(--brand-indigo)]">{accent}</span>
          </>
        )}
      </span>
      <SetSwitch label={`${RM_CHANNEL_LABEL[step.channel]} ${whenLabel(step, section.kind)}`} checked={step.on} onChange={onToggle} />
    </div>
  );
}

/** One row of card 02 — the missed follow-up, or a procedure's checks. */
function AfterRow({
  title,
  tag,
  words,
  changedByPractice,
  on,
  last,
  onEdit,
  onToggle,
}: {
  title: string;
  tag: string;
  words: string;
  changedByPractice?: boolean;
  on: boolean;
  last?: boolean;
  onEdit?: (() => void) | undefined;
  onToggle?: ((on: boolean) => void) | undefined;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-3.5"
      style={last === true ? undefined : { borderBottom: '1px solid #edf0f6' }}
    >
      <span className="w-[190px] shrink-0">
        <span className="block text-[13px] font-semibold text-[var(--text-primary)]">{title}</span>
        <span className="mt-0.5 block text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
          {tag}
        </span>
      </span>
      <span className="min-w-0 flex-1 text-[12.5px] text-[var(--text-secondary)]">
        {words}
        {/* Amber, and it says who changed it — a practice override presented as
            ours is a lie about where the default came from. */}
        {changedByPractice === true ? (
          <span className="ml-1 text-[var(--warn-amber)]">· changed by this practice</span>
        ) : null}
      </span>
      {onEdit === undefined ? (
        <span className="w-[56px] shrink-0" />
      ) : (
        <button
          type="button"
          onClick={onEdit}
          className="w-[56px] shrink-0 text-left text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
        >
          Edit →
        </button>
      )}
      {onToggle === undefined ? (
        <span className={cn('shrink-0 text-[11.5px] text-[var(--text-muted)]')}>—</span>
      ) : (
        <SetSwitch label={title} checked={on} onChange={onToggle} />
      )}
    </div>
  );
}

export { SetSubCard };
